import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Image,
  Flex,
  Skeleton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
} from '@chakra-ui/react'
import Link from 'next/link'
import { useGeoLocation } from '@/hooks/useGeoLocation'
import { Blurhash } from 'react-blurhash'
import { useTRPCClient } from '@/api/trpc/context'
import { useQuery } from '@tanstack/react-query'
import { create } from 'zustand'
import { BsChevronDown, BsClockHistory, BsController, BsLockFill, BsSearch } from 'react-icons/bs'
import { PiLightningBold, PiNumberSquareSevenBold, PiVideoCameraBold } from 'react-icons/pi'
import { useCashierFullBalance } from '@/context/CashierProvider'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { useTranslation } from 'next-i18next/pages'

const ImageBlurhash = ({ hash, width, height, punch = 1, ...props }: any) => {
  const [isLoaded, setIsLoaded] = useState(false)

  return (
    <>
      {!isLoaded && (
        <Blurhash
          hash={hash || 'eQKL2hw|E.NHN2~Ur@pIS4RP0qV?X8ogs6-nNaM_w{j;icS$xZs9S$'}
          width={width}
          height={height}
          punch={punch}
        />
      )}
      <Image hidden={!isLoaded} width={width} height={height} {...props} onLoad={() => setIsLoaded(true)} />
    </>
  )
}

interface Game {
  id: string
  slug: string
  name: string
  provider_name: string
  brand_color: string | null
  thumbnail_url: string | null
  thumbnail_blurHash: string | null
  slider_url: string | null
  demo: boolean
  demo_url: string | null
  geo_restriction: any[]
  geo_blocking: any[]
  bonus_buy: boolean
  denom_support: any[]
  currency_support: any[]
  volatility: number
  gameProviderLibraryId: number
  status: string
  extra: {}
  category: string[]
  rtp: number
  rtp_wager: number
  sort_order: number
  created_at: Date
  updated_at: Date
  game_provider: {
    id: number
    provider: string
    game_provider_id: string
    status: string
    extra: {}
    created_at: Date
    updated_at: Date
  }
}

type GameCategory = 'slots' | 'crash' | 'live'

const gameCategoryOptions: Array<{ id: GameCategory; labelKey: string; icon: React.ElementType }> = [
  // { id: 'slots', labelKey: 'game-zone.category-slots', icon: PiNumberSquareSevenBold },
  // { id: 'crash', labelKey: 'game-zone.category-crash', icon: PiLightningBold },
  // { id: 'live', labelKey: 'game-zone.category-live', icon: PiVideoCameraBold },
]

interface GamesSearchResponse {
  games: Game[]
  pagination: {
    page: number
    pageSize: number
    hasMore: boolean
  }
  filters: {
    providers: string[]
  }
}

interface GameZoneGamesCacheState {
  pageCache: Record<string, GamesSearchResponse>
  providersCache: string[] | null
  setPageCache: (key: string, value: GamesSearchResponse) => void
  getPageCache: (key: string) => GamesSearchResponse | undefined
  setProvidersCache: (providers: string[]) => void
}

const useGameZoneGamesCacheStore = create<GameZoneGamesCacheState>((set, get) => ({
  pageCache: {},
  providersCache: null,
  setPageCache: (key, value) =>
    set((state) => ({
      pageCache: {
        ...state.pageCache,
        [key]: value,
      },
    })),
  getPageCache: (key) => get().pageCache[key],
  setProvidersCache: (providers) => set({ providersCache: providers }),
}))

const buildGamesCacheKey = ({
  page,
  pageSize,
  name,
  providers,
  category,
}: {
  page: number
  pageSize: number
  name?: string
  providers?: string[]
  category: GameCategory
}) => {
  return JSON.stringify({
    page,
    pageSize,
    name: name || '',
    providers: providers || [],
    category,
  })
}

const getDataPriority = (game: Game) => {
  return game.sort_order
}

const sortGamesByPriority = (a: Game, b: Game) => {
  const dataPriorityDelta = getDataPriority(b) - getDataPriority(a)
  if (dataPriorityDelta !== 0) {
    return dataPriorityDelta
  }

  return a.name.localeCompare(b.name)
}

const useDisplayGames = (games: Game[]) => {
  return useMemo(() => {
    return [...games].filter((game) => getDataPriority(game) !== Number.MAX_SAFE_INTEGER).sort(sortGamesByPriority)
  }, [games])
}

const GameCard: React.FC<{ game: Game; href: string }> = ({ game, href }) => {
  const { t } = useTranslation('common')
  const { country } = useGeoLocation()

  const isRestricted = game.geo_restriction?.includes(country?.toLocaleUpperCase() || '')
  const isBlocked = game.geo_blocking?.includes(country?.toLocaleUpperCase() || '')
  const isUnavailableInRegion = isRestricted || isBlocked

  const card = (
    <Box
      _hover={{ transform: isUnavailableInRegion ? 'none' : 'translateY(-2px)' }}
      transition="transform 0.2s ease"
      cursor={isUnavailableInRegion ? 'default' : 'pointer'}
      position="relative"
      borderRadius="20px"
      overflow="hidden"
      h="210px"
      w="160px"
      bgGradient="linear(141deg, #403F8D 2.2%, rgba(64, 63, 141, 0) 98.3%)"
      role="group"
    >
      <ImageBlurhash
        hash={game.thumbnail_blurHash}
        width="100%"
        height="100%"
        punch={1}
        src={game.thumbnail_url}
        alt={game.name}
        objectFit="cover"
      />

      {isUnavailableInRegion ? (
        <Flex
          position="absolute"
          inset={0}
          bg="rgba(18,18,54,0.90)"
          align="center"
          justify="center"
          direction="column"
          gap={1.5}
          px={3}
          textAlign="center"
          color="white"
        >
          <BsLockFill size={16} color="#DCD9FF" />
          <Text fontSize="12px" fontWeight={700} lineHeight="1.2" color="ninja.100" maxW="96px">
            {t('game-zone.not-available-region')}
          </Text>
        </Flex>
      ) : (
        <Flex
          position="absolute"
          insetX={0}
          insetY={0}
          p={3}
          direction="column"
          align="center"
          justify="center"
          bg="linear-gradient(180deg, rgba(18,18,54,0.80) 27.55%, #121236 100%)"
          opacity={{ base: 0, md: 0 }}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.2s ease"
        >
          <Button
            h="48px"
            minW="96px"
            px={4}
            borderRadius="12px"
            bgGradient="linear(to-r, #8E83FB, #5D52EE)"
            color="white"
            fontSize="13px"
            fontWeight={700}
            _hover={{ bgGradient: 'linear(to-r, #9B90FF, #6A60F8)' }}
          >
            <HStack spacing={2}>
              <BsController size={16} />
              <Text>{t('game-zone.play')}</Text>
            </HStack>
          </Button>

          <VStack position="absolute" bottom="10px" spacing={1.5}>
            {/* {game.rtp > 0 && (
              <HStack
                h="24px"
                w="98px"
                px={2}
                justify="space-between"
                bg="ninja.500"
                borderRadius="8px"
                color="ninja.300"
                fontSize="10px"
                fontWeight={600}
              >
                <Text>RTP</Text>
                <Text color="white">{Number.isFinite(game.rtp) ? `${game.rtp.toFixed(1)}%` : '--'}</Text>
              </HStack>
            )} */}
          </VStack>
        </Flex>
      )}
    </Box>
  )

  if (isUnavailableInRegion) {
    return card
  }

  return (
    <Link href={href} scroll={false} style={{ display: 'block' }}>
      {card}
    </Link>
  )
}

export const GamesTemplate = () => {
  const { t } = useTranslation('common')
  const trpc = useTRPCClient()
  const modal = useAppModal()

  const { gameBalance, hasGameBalance, hasGameBalancePrice } = useCashierFullBalance()
  const [selectedCategory, setSelectedCategory] = useState<GameCategory>('slots')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProviders, setSelectedProviders] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 50

  const normalizedSearch = searchQuery.trim()
  const normalizedProviders = useMemo(() => [...selectedProviders].sort(), [selectedProviders])
  const providerFilterKey = normalizedProviders.join('|')

  useEffect(() => {
    setPage(1)
  }, [normalizedSearch, providerFilterKey, selectedCategory])

  const { data, isFetching } = useQuery({
    queryKey: ['games', selectedCategory, normalizedSearch, providerFilterKey, page],
    queryFn: async () => {
      const requestParams = {
        page,
        pageSize,
        name: normalizedSearch || undefined,
        providers: normalizedProviders.length > 0 ? normalizedProviders : undefined,
        category: selectedCategory,
      }

      const cacheKey = buildGamesCacheKey(requestParams)
      const cacheStore = useGameZoneGamesCacheStore.getState()
      const cachedPage = cacheStore.getPageCache(cacheKey)

      if (cachedPage) {
        return cachedPage
      }

      const shouldRequestFilters = !cacheStore.providersCache

      const response = (await trpc.public.games.search.query({
        ...requestParams,
        filters: shouldRequestFilters,
      })) as GamesSearchResponse

      useGameZoneGamesCacheStore.getState().setPageCache(cacheKey, response)

      if (shouldRequestFilters) {
        useGameZoneGamesCacheStore.getState().setProvidersCache(response.filters?.providers || [])
      }

      return response
    },
    staleTime: 5 * 60 * 1000,
  })

  const providerOptions = useMemo(() => {
    const providersCache = useGameZoneGamesCacheStore.getState().providersCache
    return providersCache || data?.filters?.providers || []
  }, [data?.filters?.providers])

  const loadedGames = useMemo(() => {
    const uniqueGames = new Map<string, Game>()

    for (let currentPage = 1; currentPage <= page; currentPage += 1) {
      const cacheKey = buildGamesCacheKey({
        page: currentPage,
        pageSize,
        name: normalizedSearch || undefined,
        providers: normalizedProviders.length > 0 ? normalizedProviders : undefined,
        category: selectedCategory,
      })
      const pageData = useGameZoneGamesCacheStore.getState().getPageCache(cacheKey)

      if (!pageData) {
        continue
      }

      pageData.games.forEach((game) => {
        if (!uniqueGames.has(game.id)) {
          uniqueGames.set(game.id, game)
        }
      })
    }

    return Array.from(uniqueGames.values())
  }, [data, page, normalizedSearch, normalizedProviders, selectedCategory])

  const displayGames = useDisplayGames(loadedGames)
  const gameZoneFunds = useMemo(() => gameBalance.toNumber() || 0, [gameBalance])

  const formattedGameZoneFunds = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(gameZoneFunds)
  }, [gameZoneFunds])

  const toggleProvider = (provider: string) => {
    setSelectedProviders((prev) =>
      prev.includes(provider) ? prev.filter((item) => item !== provider) : [...prev, provider]
    )
  }

  const selectCategory = (category: GameCategory) => {
    if (category === selectedCategory) {
      return
    }

    setSelectedCategory(category)
    setPage(1)
  }

  return (
    <Box minHeight="100vh" py={{ base: 2, md: 4 }}>
      <Box px={{ base: 0, md: 0 }} py={{ base: 0, md: 0 }} w="100%">
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', lg: 'center' }}
          gap={{ base: 4, lg: 6 }}
          flexDir={{ base: 'column', lg: 'row' }}
          mb={6}
        >
          <Flex w={{ base: 'full', lg: 'auto' }} align="center" justify="space-between" gap={3}>
            <Text color="white" fontSize={{ base: '30px', md: '40px' }} lineHeight="1" fontWeight={700}>
              {t('game-zone.all-games')}
            </Text>
            <Button
              as={Link}
              href="/game-zone/history"
              display={{ base: 'inline-flex', lg: 'none' }}
              h="36px"
              px={2}
              variant="ghost"
              color="ninja.300"
              fontSize="13px"
              fontWeight={600}
              leftIcon={<BsClockHistory size={15} />}
              _hover={{ bg: 'ninja.600', color: 'ninja.100', textDecoration: 'none' }}
            >
              {t('game-zone.history')}
            </Button>
          </Flex>

          <HStack w={{ base: 'full', lg: 'auto' }} spacing={2} align="center">
            {hasGameBalance && (
              <HStack
                align="center"
                justify="space-between"
                spacing={3}
                h="48px"
                w={{ base: 'full', sm: 'auto' }}
                borderRadius="16px"
                px={2}
                bg="ninja.600"
                border="1px solid"
                borderColor="ninja.500"
                minW={{ base: 'unset', sm: '228px' }}
              >
                <Box>
                  <Flex h="full" align="center" gap={3}>
                    <Box
                      boxSize="20px"
                      borderRadius="full"
                      bgGradient="linear(to-r, #8E83FB, #5D52EE)"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      lineHeight={0}
                      color="white"
                    >
                      <BsController size={10} />
                    </Box>

                    <VStack align="flex-start" spacing={0}>
                      <Text color="white" fontWeight={700} fontSize="14px" lineHeight="1">
                        {hasGameBalancePrice ? `≈ $${formattedGameZoneFunds}` : '—'}
                      </Text>
                      <Text color="ninja.200" fontSize="10px" fontWeight={600} lineHeight="1.2">
                        {t('game-zone.your-funds')}
                      </Text>
                    </VStack>
                  </Flex>
                </Box>

                <Button
                  h="40px"
                  borderRadius="14px"
                  bgGradient="linear(to-r, #8E83FB, #5D52EE)"
                  color="white"
                  fontSize="14px"
                  fontWeight={700}
                  _hover={{ bgGradient: 'linear(to-r, #9B90FF, #6A60F8)' }}
                  onClick={() => modal.openModalNew(AppModals.GameZoneWithdraw)}
                >
                  {t('game-zone.withdraw')}
                </Button>
              </HStack>
            )}

            <Button
              as={Link}
              href="/game-zone/history"
              display={{ base: 'none', lg: 'inline-flex' }}
              h="36px"
              px={2}
              variant="ghost"
              color="ninja.300"
              fontSize="13px"
              fontWeight={600}
              leftIcon={<BsClockHistory size={15} />}
              _hover={{ bg: 'ninja.600', color: 'ninja.100', textDecoration: 'none' }}
            >
              {t('game-zone.history')}
            </Button>
          </HStack>
        </Flex>

        <Flex gap={3} mb={5} flexWrap="wrap" align="flex-start">
          <InputGroup maxW={{ base: 'full', md: '460px' }} flex="1 1 280px">
            <InputLeftElement pointerEvents="none" color="ninja.200" h="48px" pl={1}>
              <BsSearch size={17} />
            </InputLeftElement>
            <Input
              h="48px"
              pl="44px"
              pr={4}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('game-zone.search')}
              color="ninja.100"
              bg="ninja.600"
              borderRadius="12px"
              border="1px solid"
              borderColor="transparent"
              fontFamily="body"
              fontSize="15px"
              fontWeight={500}
              lineHeight="normal"
              sx={{ caretColor: '#A8A3FF' }}
              _placeholder={{ color: 'ninja.200', opacity: 1, fontWeight: 500 }}
              _hover={{ bg: 'ninja.500', borderColor: 'ninja.400' }}
              _focusVisible={{ bg: 'ninja.600', borderColor: 'brand.link', boxShadow: '0 0 0 1px #A8A3FF' }}
            />
          </InputGroup>

          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              h="48px"
              w={{ base: 'full', sm: 'auto' }}
              minW={{ base: 'full', sm: '200px' }}
              px={4}
              bg="ninja.600"
              border="1px solid"
              borderColor="transparent"
              color="ninja.200"
              borderRadius="12px"
              fontFamily="body"
              fontSize="14px"
              fontWeight={700}
              textAlign="left"
              rightIcon={<BsChevronDown size={14} />}
              _hover={{ bg: 'ninja.500', borderColor: 'ninja.400' }}
              _active={{ bg: 'ninja.500', borderColor: 'brand.link' }}
            >
              {selectedProviders.length > 0
                ? t('game-zone.operators-selected', { count: selectedProviders.length })
                : t('game-zone.operators-all')}
            </MenuButton>

            <MenuList
              minW={{ base: 'calc(100vw - 32px)', sm: '240px' }}
              maxH="320px"
              overflowY="auto"
              p={1.5}
              bg="ninja.600"
              border="1px solid"
              borderColor="ninja.500"
              borderRadius="12px"
              boxShadow="0 16px 40px rgba(5, 4, 31, 0.45)"
              css={{
                '&::-webkit-scrollbar': { width: '5px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#434172', borderRadius: '8px' },
              }}
            >
              {providerOptions.map((provider) => (
                <MenuItem
                  key={provider}
                  bg="transparent"
                  minH="42px"
                  px={3}
                  py={2}
                  borderRadius="8px"
                  _hover={{ bg: 'ninja.500' }}
                  _focus={{ bg: 'ninja.500' }}
                  onClick={() => toggleProvider(provider)}
                >
                  <Checkbox
                    isChecked={selectedProviders.includes(provider)}
                    pointerEvents="none"
                    colorScheme="purple"
                    fontSize="14px"
                    fontWeight={600}
                    color="ninja.100"
                    spacing={3}
                    sx={{
                      '.chakra-checkbox__control': {
                        width: '20px',
                        height: '20px',
                        borderRadius: '6px',
                      },
                    }}
                  >
                    <Text as="span" textTransform="capitalize">
                      {provider}
                    </Text>
                  </Checkbox>
                </MenuItem>
              ))}
              {selectedProviders.length > 0 && (
                <MenuItem
                  minH="40px"
                  px={3}
                  borderRadius="8px"
                  bg="transparent"
                  color="ninja.200"
                  fontSize="13px"
                  fontWeight={600}
                  _hover={{ bg: 'ninja.500', color: 'ninja.100' }}
                  onClick={() => setSelectedProviders([])}
                >
                  {t('game-zone.clear-all')}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </Flex>

        <Flex role="group" aria-label={t('game-zone.categories')} w="full" gap={2} mb={5} flexWrap="wrap">
          {gameCategoryOptions.map((category) => {
            const isActive = selectedCategory === category.id
            const CategoryIcon = category.icon

            return (
              <Button
                key={category.id}
                type="button"
                aria-pressed={isActive}
                variant="unstyled"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flex={{ base: '1 1 0', sm: '0 0 auto' }}
                minW={{ base: 0, sm: '108px' }}
                h="40px"
                px={{ base: 2, sm: 3 }}
                borderRadius="10px"
                border="1px solid"
                borderColor={isActive ? '#6B69C4' : 'ninja.500'}
                bg={isActive ? '#3D3C70' : 'ninja.600'}
                color="white"
                fontSize="13px"
                fontWeight={700}
                transition="all 0.15s ease"
                _hover={{
                  bg: isActive ? '#4A4890' : 'ninja.500',
                  borderColor: isActive ? '#7B77DA' : 'ninja.400',
                  transform: 'translateY(-1px)',
                }}
                _active={{
                  bg: isActive ? '#4A4890' : 'ninja.500',
                  transform: 'translateY(0)',
                }}
                _focusVisible={{ boxShadow: '0 0 0 2px #A8A3FF' }}
                onClick={() => selectCategory(category.id)}
              >
                <HStack spacing={{ base: 1.5, sm: 2 }}>
                  <CategoryIcon size={15} aria-hidden="true" />
                  <Text as="span">{t(category.labelKey)}</Text>
                </HStack>
              </Button>
            )
          })}
        </Flex>

        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
          columnGap={4}
          rowGap={4}
          justifyItems="center"
        >
          {displayGames.map((game) => (
            <GameCard key={game.id} game={game} href={`/game-zone/${encodeURIComponent(game.id)}`} />
          ))}
          {isFetching && (
            <>
              {Array.from({ length: 24 }).map((_, i) => (
                <Skeleton
                  key={i}
                  width="160px"
                  height="210px"
                  borderRadius="16px"
                  startColor="ninja.400"
                  endColor="ninja.500"
                />
              ))}
            </>
          )}
        </Box>

        {!displayGames.length && (
          <Flex py={16} justify="center">
            <Text color="ninja.200" fontSize="14px" fontWeight={600}>
              {t('game-zone.no-games')}
            </Text>
          </Flex>
        )}

        {data?.pagination.hasMore && (
          <Flex justify="center" mt={8}>
            <Button isLoading={isFetching} onClick={() => setPage((prev) => prev + 1)}>
              {t('game-zone.view-more')}
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  )
}
